import {
  err,
  markWatchedPath,
  ok,
  parseHistoryPage,
  removeFromHistoryPath,
  resolveInstanceUrl,
  withQuery,
  historyWireParams,
  CLEAR_HISTORY_PATH,
  HISTORY_PATH,
  type AbortLike,
  type AuthScope,
  type HistoryPage,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { authGetUrl, authWrite, type AuthApiProblem } from './auth';

/**
 * §8's History row — one read and three writes, all four of them on `/api/v1/auth/history`.
 *
 * Every decision about *what to send* is in `history/list.ts`, which is not tidiness here but the only
 * protection available: this endpoint answers `200` to four different malformed requests and `204` to a
 * destructive one, so nothing about a response tells a caller it asked the wrong thing. In particular
 * neither path below is built by interpolation — an empty video id turns `DELETE this row` into
 * `DELETE everything`, with a byte-identical response.
 */

/**
 * A video id this build refuses to put in a URL. Its own arm rather than a thrown error, because the
 * caller has a row on screen and needs a sentence for it.
 *
 * Reachable in exactly one way — an id that got into the account's history without passing through
 * `parseHistoryPage` (Invidious's own web UI writing something odd, a future id format). The point is
 * that it is *reachable*, and the alternative outcome is a `400` at best and a cleared history at worst.
 */
export interface BadVideoIdProblem {
  readonly kind: 'bad-video-id';
  readonly videoId: string;
}

export type HistoryProblem = AuthApiProblem | BadVideoIdProblem;

/**
 * One page of the id list.
 *
 * Both wire parameters are always sent, including on page 1 — `historyWireParams` explains why: an
 * omitted `max_results` means "the size the reader configured in Invidious", which is server state this
 * client cannot see and which would silently break the terminating proof.
 */
export async function fetchHistoryPage(
  platform: Platform,
  page: number,
  maxResults: number,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<HistoryPage, AuthApiProblem>> {
  const base = resolveInstanceUrl(platform.config, HISTORY_PATH);
  if (base === null) return err({ kind: 'bad-url', path: HISTORY_PATH });

  const url = withQuery(base, historyWireParams(page, maxResults));
  if (url === null) return err({ kind: 'bad-url', path: HISTORY_PATH });

  const raw = await authGetUrl(platform, HISTORY_PATH, url, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);
  return ok(parseHistoryPage(raw.value));
}

/**
 * The whole id list in one request, which is what clear-all and a "how many?" both need.
 *
 * Affordable precisely because the list is ids: 68 rows measured at **953 bytes**. `max_results` is set
 * far above any plausible history rather than paged, since there is no cap — `max_results=10000` returned
 * everything — and a partial answer would make clear-all quietly incomplete.
 */
export async function fetchWholeHistory(
  platform: Platform,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<HistoryPage, AuthApiProblem>> {
  return await fetchHistoryPage(platform, 1, 100_000, grantedScopes, signal);
}

/**
 * `POST /api/v1/auth/history/{id}` — §8's watched-state mirror. `204`, nothing to read.
 *
 * The scope is passed rather than left to the instance, which is `authWrite`'s rule for every mutation:
 * `Invalid scope` and `Token is expired` share a status, and a write that may or may not have happened is
 * not a question a retry can answer.
 */
export async function markWatched(
  platform: Platform,
  videoId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, HistoryProblem>> {
  const path = markWatchedPath(videoId);
  if (path === null) return err({ kind: 'bad-video-id', videoId });

  const result = await authWrite(platform, 'POST', path, null, grantedScopes, signal);
  return result.ok ? ok(null) : err(result.error);
}

/**
 * `DELETE /api/v1/auth/history/{id}` — remove one row. `204`, and idempotent: an id that is not there
 * answers the same, so a row already removed in another tab is not an error.
 *
 * **The `null` guard is the load-bearing line in this file.** `removeFromHistoryPath` refuses anything
 * that is not a video id because the path it would otherwise produce — `/api/v1/auth/history/` — clears
 * the entire history and answers the same `204` this call expects.
 */
export async function removeFromHistory(
  platform: Platform,
  videoId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, HistoryProblem>> {
  const path = removeFromHistoryPath(videoId);
  if (path === null) return err({ kind: 'bad-video-id', videoId });

  const result = await authWrite(platform, 'DELETE', path, null, grantedScopes, signal);
  return result.ok ? ok(null) : err(result.error);
}

/**
 * `DELETE /api/v1/auth/history` — clear everything, in **one** request.
 *
 * §8 and this repo's notes both had this as "iterate the list", which measurement replaced: the bare path
 * wipes the history wholesale. So clear-all is one call rather than one per row, which also removes the
 * partial-failure state a 68-request loop would have had.
 */
export async function clearHistory(
  platform: Platform,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, AuthApiProblem>> {
  const result = await authWrite(platform, 'DELETE', CLEAR_HISTORY_PATH, null, grantedScopes, signal);
  return result.ok ? ok(null) : err(result.error);
}
